import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { IDefinitionSearchInfo } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { IPlantCategory } from '@/interfaces/powerMarkets/plant/IPlantCategory.ts';
import { IPlantsByCountry } from '@/interfaces/powerMarkets/plant/IPlantsByCountry.ts';
import { IPlantsStore } from '@/interfaces/powerMarkets/IPlantsStore.ts';
import { createEmptyStaticStore, IStaticStore, ITimeZones } from '@/interfaces/powerMarkets/IStaticStore.ts';
import { TApiBaseUrlKey } from '@/api/useApi.ts';
import { dataStoreApi } from '@/api/powerMarkets/dataStoreApi.ts';
import { ICountry } from '@/interfaces/general/ICountry.ts';
import { IEarliestDateInBuffer } from '@/interfaces/powerMarkets/static/IEarliestDateInBuffer.ts';
import { IPeriodData } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { tryCatch } from '@/helpers/tryCatch.ts';
import { adminApi } from '@/api/admin/adminApi.ts';
import { IAvailableShortTermPowerCountry } from '@/interfaces/admin/IAvailableShortTermPowerCountry.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useLiveTableStore } from './liveTableStore.ts';

export const useDataStore = defineStore('dataStore', () => {
    const liveTableStore = useLiveTableStore();
    const userSettingsStore = useUserSettingsStore();

    const staticStore = shallowRef<IStaticStore>(createEmptyStaticStore());
    const staticStoreFetchFailed = ref<boolean>(false);
    const plantStore = shallowRef<IPlantsStore | null>(null);
    const searchDefinitions = shallowRef<Array<IDefinitionSearchInfo> | null>(null);
    const plantsByEndpoint = ref<Record<string, IPlantsByCountry>>({});
    const periodData = shallowRef<IPeriodData | null>(null);
    const earliestBufferPromises = ref<Record<string, Promise<void> | undefined>>({});
    const timezones = ref<ITimeZones | null>(null);
    const availableShortTermPowerCountries = ref<Array<IAvailableShortTermPowerCountry>>([]);
    const hasLoadedAvailableShortTermPowerCountries = ref<boolean>(false);

    async function getAvailableShortTermPowerCountries (): Promise<void> {
        if (hasLoadedAvailableShortTermPowerCountries.value) return;

        availableShortTermPowerCountries.value = await adminApi.getAvailableShortTermPowerCountries();
        hasLoadedAvailableShortTermPowerCountries.value = true;
    }

    async function getTimezones (): Promise<ITimeZones | null> {
        if (!timezones.value) {
            const { error, data } = await tryCatch(dataStoreApi.getTimeZones());
            if (error) console.log('Error fetching timezones:', error);
            else timezones.value = data.data;
        }
        return timezones.value;
    }

    const nonOwnerPlants = computed<Array<IPlant>>(() => Object.values(keyedPlants.value));

    const keyedPlants = computed<Record<string, IPlant>>(() => {
        if (!plantStore.value) return {};
        const plants: Record<string, IPlant> = {};

        Object.values(plantStore.value.allPlants).forEach((category: IPlantCategory) => {
            if (category.categoryName === 'Owner') return;
            category.plants.forEach(plant => {
                plants[plant.id] = plant;
            });
        });

        return plants;
    });

    const allPlants = computed<Array<IPlant>>(() => {
        if (!plantStore.value) return [];
        const plants: Array<IPlant> = [];

        Object.values(plantStore.value.allPlants).forEach((category: IPlantCategory) => {
            plants.push(...category.plants);
        });

        return plants;
    });

    // Non GB
    const allPlantsByCountry = computed<Record<string, Array<IPlant>>>(() => {
        const plantsByCountry: Record<string, Array<IPlant>> = {};

        Object.keys(plantsByEndpoint.value).forEach(key => {
            if (plantsByEndpoint.value[key]) {
                Object.keys(plantsByEndpoint.value[key].plantList).forEach(country => {
                    if (plantsByEndpoint.value[key].plantList[country]) {
                        if (!plantsByCountry[country]) {
                            plantsByCountry[country] = [];
                        }
                        plantsByCountry[country].push(...plantsByEndpoint.value[key].plantList[country]);
                    }
                });
            }
        });

        return plantsByCountry;
    });

    const countriesById = computed(() => {
        const countryIndex: Record<string, ICountry> = {};

        staticStore.value.countries.forEach(country => {
            countryIndex[country.id] = country;
        });
        return countryIndex;
    });

    async function fetchStaticStore (): Promise<void> {
        staticStoreFetchFailed.value = false;
        const { error, data: staticStoreResponse } = await tryCatch(dataStoreApi.getStaticStore());
        if (error || !staticStoreResponse.data) {
            staticStoreFetchFailed.value = true;
            return;
        }
        staticStore.value = {
            ...staticStoreResponse.data,
            dashboardPresets: staticStoreResponse.data.dashboardPresets ?? [],
            endpointToEarliestBufferDate: {},
            apiEndpointBaseUrlOverrides: staticStoreResponse.data.apiEndpointBaseUrlOverrides ?? {},
        };
        liveTableStore.updateAllLiveTableOptions(staticStore.value.liveTableOptions);
        fetchEarliestDatesInBuffer();
    }

    async function fetchEarliestDatesInBuffer (): Promise<void> {
        staticStore.value.jointSeriesEndpoints.forEach((endpoint) => {
            const earliestDatePromise = fetchEarliestDateInBuffer(endpoint);
            earliestBufferPromises.value[endpoint.toLowerCase()] = earliestDatePromise;
        });
    }

    async function fetchEarliestDateInBuffer (endpoint: string): Promise<void> {
        if (import.meta.env.MODE === 'production' && endpoint.includes('local')) return;
        const earliestDateInBufferResponse = await dataStoreApi.getEarliestDateInBuffer(endpoint);
        const dataResponse = earliestDateInBufferResponse.data as IEarliestDateInBuffer;
        const earliestDayInBuffer = dataResponse.day === null ? null : new Date(dataResponse.year, dataResponse.month - 1, dataResponse.day);
        staticStore.value.endpointToEarliestBufferDate[endpoint.toLowerCase()] = { earliestDayInBuffer };
        delete earliestBufferPromises.value[endpoint.toLowerCase()];
    }

    async function fetchPlantStore (): Promise<void> {
        dataStoreApi.getPlantStore().then(response => {
            if (!response) return;
            // TODO: add error handling
            plantStore.value = response.data;
        });
    }

    async function fetchPlantsByEndpoint (endpoint: TApiBaseUrlKey): Promise<void> {
        dataStoreApi.getPlantByEndpointKey(endpoint).then(response => {
            if (!response) return;
            plantsByEndpoint.value[endpoint] = response.data;
        });
    }

    function updatePeriodData (val: IPeriodData): void {
        periodData.value = val;
    }

    function updateStaticStore (val: IStaticStore): void {
        staticStore.value = val;
    }

    function updatePlantsStore (val: IPlantsStore): void {
        plantStore.value = val;
    }

    function updatePlantList (id: string, val: IPlantsByCountry): void {
        plantsByEndpoint.value[id] = val;
    }

    function updateSearchDefinitions (val: Array<IDefinitionSearchInfo>): void {
        searchDefinitions.value = val;
    }

    function getPlantNameById (plantId: string): string {
        const plantDisplayOption = userSettingsStore.userSettings.otherSettings.data.plantDisplayOption ?? 'id';
        const plant = keyedPlants.value[plantId];
        if (!plant) return plantId;
        if (plantDisplayOption === 'name') return plant.name;
        return plantId;
    }

    return {
        nonOwnerPlants,
        getPlantNameById,
        availableShortTermPowerCountries,
        allPlants,
        allPlantsByCountry,
        plantStore,
        plantsByEndpoint,
        staticStore,
        staticStoreFetchFailed,
        searchDefinitions,
        periodData,
        countriesById,
        earliestBufferPromises,
        fetchPlantStore,
        fetchPlantsByEndpoint,
        fetchStaticStore,
        updatePlantList,
        updatePlantsStore,
        updateSearchDefinitions,
        updateStaticStore,
        updatePeriodData,
        getTimezones,
        getAvailableShortTermPowerCountries,
    };
});
